import { createContext, useContext, type ReactNode } from 'react';
import { Loader2, AlertCircle, LogIn } from 'lucide-react';
import { useAuth, type AuthIdentity, type UseAuthResult } from './useAuth';

const CurrentUserContext = createContext<UseAuthResult | null>(null);

export function useCurrentUser(): UseAuthResult {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error('useCurrentUser fuera de <LoginGate>');
  return ctx;
}

export function useIdentity(): AuthIdentity {
  const { identity } = useCurrentUser();
  if (!identity) {
    throw new Error('useIdentity llamado sin sesión — wrap con LoginGate.');
  }
  return identity;
}

interface LoginGateProps {
  children: ReactNode;
}

export function LoginGate({ children }: LoginGateProps) {
  const auth = useAuth();

  if (auth.isAuthenticated && auth.identity) {
    return (
      <CurrentUserContext.Provider value={auth}>{children}</CurrentUserContext.Provider>
    );
  }

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-marzano-50 via-white to-marzano-100 overflow-hidden">
        <div className="absolute top-8 left-10 z-10">
          <PionerosLogo />
        </div>
        <img
          src="/loginpioneros.png"
          alt="Estudiantes Pioneros"
          className="absolute bottom-0 left-0 w-full object-cover object-bottom"
          style={{ maxHeight: '70vh' }}
        />
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-12 py-12">
        <div className="w-full max-w-md">
          {/* Logo solo en mobile; en desktop está en la columna izquierda. */}
          <div className="lg:hidden mb-8">
            <PionerosLogo />
          </div>

          <div className="space-y-1 mb-6">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              ¡Bienvenidos a Pioneros! <span aria-hidden>👋</span>
            </h1>
            <p className="text-sm text-slate-500">
              Inicia sesión en tu cuenta para acceder al{' '}
              <span className="font-medium text-marzano-700">Evaluador Marzano IA</span>.
            </p>
          </div>

          {auth.error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium text-red-800 text-sm">
                    No se pudo iniciar sesión
                  </div>
                  <div className="text-xs text-red-700 mt-1">{auth.error}</div>
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => void auth.login()}
            disabled={auth.loading}
            className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-lg
                       bg-marzano-600 text-white font-medium
                       hover:bg-marzano-700 active:bg-marzano-800
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors shadow-sm"
          >
            {auth.loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Resolviendo identidad…
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                Iniciar sesión con Microsoft
              </>
            )}
          </button>

          <p className="text-[11px] text-slate-400 mt-6 text-center">
            Tu acceso depende de los privilegios registrados en Pioneros.
            <br />
            Si tu cuenta no aparece, contacta al equipo Pioneros.
          </p>
        </div>
      </div>
    </div>
  );
}

function PionerosLogo() {
  return (
    <div className="leading-none">
      <div className="text-[10px] tracking-[0.35em] text-slate-600 font-light">COLEGIO</div>
      <div className="text-3xl font-serif tracking-wider text-slate-900 mt-0.5">
        PIONEROS
      </div>
    </div>
  );
}
